import { useEffect, useCallback, useRef, type MutableRefObject } from "react";
import type { Gantt } from "@bryntum/gantt-thin";

export interface UseGanttInfiniteScrollOptions {
   /** Reference to the Bryntum Gantt instance */
   ganttInstance: Gantt | null | undefined;
   /** Ref tracking whether the initial data load is complete */
   isInitialLoadCompleteRef: MutableRefObject<boolean>;
   /** Ref tracking whether all records have been loaded */
   hasAllRecordsRef: MutableRefObject<boolean>;
   /** Ref tracking whether loading is currently in progress */
   isLoadingRef: MutableRefObject<boolean>;
   /** Callback to load more data */
   onLoadMore: () => void;
   threshold?: number;
   debounceMs?: number;
}

/**
 * Hook to handle infinite scroll for Bryntum Gantt.
 * Attaches a scroll listener to the Gantt's virtual scroller and triggers
 * loading when the user scrolls near the bottom.
 *
 * Uses refs for state to ensure the scroll handler always has access to
 * current values without needing to recreate the callback.
 */
export function useGanttInfiniteScroll({
   ganttInstance,
   isInitialLoadCompleteRef,
   hasAllRecordsRef,
   isLoadingRef,
   onLoadMore,
   threshold = 200,
   debounceMs = 200,
}: UseGanttInfiniteScrollOptions): void {
   const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const handleScroll = useCallback(() => {
      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => {
         // Read current ref values at scroll time (not render time)
         if (!isInitialLoadCompleteRef.current) return;
         if (isLoadingRef.current) return;
         if (hasAllRecordsRef.current) return;
         if (!ganttInstance) return;

         const scroller = ganttInstance.scrollable;
         const scrollTop = scroller.y ?? 0;
         const scrollHeight = scroller.scrollHeight ?? 0;
         const clientHeight = scroller.clientHeight ?? 0;
         const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

         // Load more when within threshold of bottom
         if (distanceFromBottom < threshold) {
            onLoadMore();
         }
      }, debounceMs);
   }, [
      ganttInstance,
      isInitialLoadCompleteRef,
      hasAllRecordsRef,
      isLoadingRef,
      onLoadMore,
      threshold,
      debounceMs,
   ]);

   useEffect(() => {
      if (!ganttInstance) return;
      // Guard against instances that don't support event listeners (e.g., during view switches)
      if (typeof ganttInstance.on !== "function") return;

      // Attach scroll listener
      ganttInstance.on("scroll", handleScroll);

      return () => {
         // Cleanup scroll listener - guard in case instance changed or was destroyed
         if (typeof ganttInstance.un === "function") {
            ganttInstance.un("scroll", handleScroll);
         }
         if (timerRef.current) clearTimeout(timerRef.current);
      };
   }, [ganttInstance, handleScroll]);
}
